import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Repeat, Package, MapPin, User as UserIcon, Trash2, Plus, Calendar, ShoppingBag, ArrowRight, ChevronDown } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { HKMonogram } from "@/components/BrandMark";
import { useCustomerAuth, customerApi, formatApiError } from "@/context/CustomerAuthContext";
import { useProducts } from "@/context/ProductsContext";
import { useCart } from "@/context/CartContext";
import { DeliveryLocationPicker, useGoogleMapsConfig } from "@/components/DeliveryLocationPicker";
import usePageTitle from "@/hooks/usePageTitle";

/** How many days after purchase we expect a customer to refill.
 *  Simple, admin-configurable in later rounds via product.refill_days. */
const DEFAULT_REFILL_DAYS = 30;

const fmtDate = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(v); }
};
const daysBetween = (a, b) => {
  const ms = new Date(b) - new Date(a);
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};
const statusTone = (s) => ({
  paid: "bg-[#E1EFE4] text-[#2F5A38] border-[#B7D6BE]",
  pending: "bg-[#FDECDB] text-[#B85E24] border-[#F3C89A]",
  whatsapp_handoff: "bg-[#DDEEF6] text-[#1F5E7A] border-[#B0D6E5]",
  failed: "bg-[#F6DEDE] text-[#8A2323] border-[#E5B6B6]",
}[s] || "bg-[#EEE7D5] text-[#8A6E1F] border-[#D6C899]");

/* ================================================================== */
const StatCard = ({ label, value, sub, icon: Icon, testId }) => (
  <div className="hk-card p-6 flex items-start gap-4" data-testid={testId}>
    <div className="w-11 h-11 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-gold)]/40 flex items-center justify-center text-[var(--hk-orange-deep)] shrink-0">
      <Icon size={17} strokeWidth={1.6} />
    </div>
    <div>
      <div className="label-eyebrow !text-[10px]">{label}</div>
      <div className="serif mt-1 text-[26px] leading-none text-[var(--hk-ink)]" style={{ paddingBottom: "0.04em" }}>{value}</div>
      {sub && <div className="mt-1 text-[12px] text-[var(--hk-ink-3)]">{sub}</div>}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Order row with Reorder                                             */
/* ------------------------------------------------------------------ */
const OrderRow = ({ order, onReorder }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[var(--hk-border)] last:border-b-0" data-testid={`account-order-${order.id}`}>
      <div className="p-5 md:p-6 grid md:grid-cols-[1fr_auto_auto_auto] gap-4 items-center">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="serif text-[16px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
              Order #{order.id.slice(0, 8).toUpperCase()}
            </div>
            <span className={"text-[10.5px] tracking-[0.22em] uppercase px-2 py-1 rounded-full border " + statusTone(order.payment_status)}>
              {order.payment_status.replace("_", " ")}
            </span>
          </div>
          <div className="mt-2 text-[12px] text-[var(--hk-ink-3)] flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(order.created_at)}</span>
            <span>·</span>
            <span>{order.items.length} item{order.items.length > 1 ? "s" : ""}</span>
          </div>
        </div>
        <div className="serif text-[18px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
          ₹{(order.total / 100).toLocaleString("en-IN")}
        </div>
        <button
          onClick={() => onReorder(order)}
          className="hk-btn-primary !py-2 whitespace-nowrap"
          data-testid={`account-reorder-${order.id}`}
        >
          <Repeat size={14} /> Reorder
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          className="p-2 rounded-md border border-[var(--hk-border)] text-[var(--hk-ink-2)] hover:border-[var(--hk-orange)] transition-colors"
          aria-label="Toggle order details"
          data-testid={`account-order-toggle-${order.id}`}
        >
          <ChevronDown size={15} className={"transition-transform " + (open ? "rotate-180" : "")} />
        </button>
      </div>
      {open && (
        <div className="px-5 md:px-6 pb-6">
          <div className="rounded-lg bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] p-4 space-y-2">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-[13px]">
                <div className="text-[var(--hk-ink)]"><span className="text-[var(--hk-ink-3)]">{it.qty}×</span> {it.name}</div>
                <div className="text-[var(--hk-ink-2)]">₹{((it.price * it.qty) / 100).toLocaleString("en-IN")}</div>
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-[var(--hk-border)] flex items-center justify-between text-[13px]">
              <div className="text-[var(--hk-ink-3)]">Delivery to</div>
              <div className="text-[var(--hk-ink)] text-right">{order.city}, {order.state} — {order.pincode}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  My Wellness Products (refill timeline)                             */
/* ------------------------------------------------------------------ */
const WellnessTimeline = ({ orders, onReorderItem }) => {
  // Compute earliest order per slug + refill projection
  const lines = useMemo(() => {
    const today = new Date();
    const map = new Map();
    (orders || []).forEach((o) => {
      (o.items || []).forEach((it) => {
        const prev = map.get(it.slug);
        if (!prev || new Date(o.created_at) > new Date(prev.lastAt)) {
          map.set(it.slug, { slug: it.slug, name: it.name, price: it.price, lastAt: o.created_at, qty: it.qty });
        }
      });
    });
    return Array.from(map.values()).map((row) => {
      const refillDate = new Date(row.lastAt);
      refillDate.setDate(refillDate.getDate() + DEFAULT_REFILL_DAYS);
      const daysLeft = daysBetween(today, refillDate);
      const overdue = refillDate < today;
      return { ...row, refillDate, daysLeft, overdue };
    }).sort((a, b) => a.refillDate - b.refillDate);
  }, [orders]);

  if (lines.length === 0) return null;

  return (
    <section className="hk-card p-6 md:p-8" data-testid="account-wellness-section">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Your wellness routine</div>
          <h3 className="serif mt-2 text-[22px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>
            Smart refill timeline
          </h3>
          <p className="mt-2 text-[13.5px] text-[var(--hk-ink-2)] max-w-2xl leading-[1.7]">
            Based on your last order date and each product's expected {DEFAULT_REFILL_DAYS}-day cycle. We'll remind you as your refill date approaches.
          </p>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {lines.map((row) => (
          <div key={row.slug} className="p-5 rounded-xl border border-[var(--hk-border)] bg-white flex flex-col gap-3 hover:border-[var(--hk-orange)]/50 transition-colors" data-testid={`account-refill-${row.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="serif text-[16px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>{row.name}</div>
                <div className="mt-1 text-[11px] text-[var(--hk-ink-3)]">Last ordered {fmtDate(row.lastAt)}</div>
              </div>
              <div className={"text-[10.5px] tracking-[0.22em] uppercase px-2 py-1 rounded-full border " + (row.overdue ? "bg-[#F6DEDE] text-[#8A2323] border-[#E5B6B6]" : row.daysLeft <= 5 ? "bg-[#FDECDB] text-[#B85E24] border-[#F3C89A]" : "bg-[var(--hk-warm-ivory)] text-[var(--hk-ink-2)] border-[var(--hk-border)]")}>
                {row.overdue ? "Due" : `${row.daysLeft}d`}
              </div>
            </div>
            <div className="text-[12px] text-[var(--hk-ink-2)]">
              Expected refill by <span className="text-[var(--hk-ink)]">{fmtDate(row.refillDate)}</span>
            </div>
            <button
              onClick={() => onReorderItem(row)}
              className="mt-1 self-start inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[var(--hk-orange-deep)] hover:underline"
            >
              <Repeat size={12} /> Reorder now
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Addresses                                                          */
/* ------------------------------------------------------------------ */
const AddressBook = () => {
  const [addrs, setAddrs] = useState([]);
  const [form, setForm] = useState({ label: "Home", address_line1: "", address_line2: "", city: "", state: "", pincode: "" });
  const [loc, setLoc] = useState(null);
  const mapsCfg = useGoogleMapsConfig();
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await customerApi.get("/customer/addresses");
      setAddrs(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        latitude: loc?.latitude ?? undefined,
        longitude: loc?.longitude ?? undefined,
        google_maps_url: loc?.google_maps_url || undefined,
        location_source: loc?.location_source || undefined,
      };
      const { data } = await customerApi.post("/customer/addresses", payload);
      setAddrs(data || []);
      setForm({ label: "Home", address_line1: "", address_line2: "", city: "", state: "", pincode: "" });
      setLoc(null);
      setShowForm(false);
      toast.success("Address saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (idx) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      const { data } = await customerApi.delete(`/customer/addresses/${idx}`);
      setAddrs(data || []);
      toast.success("Address deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <section className="hk-card p-6 md:p-8" data-testid="account-addresses-section">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Delivery</div>
          <h3 className="serif mt-2 text-[22px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>Address book</h3>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="hk-btn-ghost !py-2" data-testid="account-add-address-btn">
          <Plus size={14} /> {showForm ? "Cancel" : "Add address"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={add} className="mt-6 grid md:grid-cols-2 gap-3 p-5 rounded-xl bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)]" data-testid="account-address-form">
          <input className="hk-input" placeholder="Label (Home / Office)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <input className="hk-input" placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} required />
          <input className="hk-input md:col-span-2" placeholder="Address line 1" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} required />
          <input className="hk-input md:col-span-2" placeholder="Address line 2 (optional)" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
          <input className="hk-input" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
          <input className="hk-input" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} required />
          <div className="md:col-span-2">
            <DeliveryLocationPicker
              compact
              config={mapsCfg}
              value={loc}
              onChange={setLoc}
              onAddressParts={(p) => setForm((f) => ({ ...f, address_line1: p.address_line1 || f.address_line1, city: p.city || f.city, state: p.state || f.state, pincode: p.pincode || f.pincode }))}
              testid="account-maps"
            />
          </div>
          <button type="submit" disabled={busy} className="hk-btn-primary md:col-span-2 justify-center">
            {busy ? "Saving…" : "Save address"}
          </button>
        </form>
      )}

      {addrs.length === 0 ? (
        <div className="mt-6 py-8 text-center text-[13.5px] text-[var(--hk-ink-3)]">No addresses saved yet. Add one to check out faster next time.</div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-3">
          {addrs.map((a, i) => (
            <div key={i} className="p-5 rounded-xl border border-[var(--hk-border)] bg-white flex items-start justify-between gap-3" data-testid={`account-address-${i}`}>
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[var(--hk-gold-deep)]">{a.label || "Home"}</div>
                <div className="mt-2 text-[13.5px] leading-[1.75] text-[var(--hk-ink)]">
                  {a.address_line1}{a.address_line2 && `, ${a.address_line2}`}<br />
                  {a.city}, {a.state} — {a.pincode}
                </div>
                {(a.latitude != null || a.google_maps_url) && (
                  <a href={a.google_maps_url || `https://www.google.com/maps?q=${a.latitude},${a.longitude}`} target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[11px] text-[var(--hk-orange-deep)] underline" data-testid={`account-address-maps-${i}`}>
                    <MapPin size={11} /> Map location saved
                  </a>
                )}
              </div>
              <button onClick={() => remove(i)} className="p-2 text-[var(--hk-ink-3)] hover:text-red-600" aria-label="Delete address" data-testid={`account-delete-address-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/* ================================================================== */
/*  Main page                                                          */
/* ================================================================== */
export const CustomerAccountPage = () => {
  usePageTitle("My Account");
  const { user, loading, logout } = useCustomerAuth();
  const { allProducts } = useProducts();
  const cart = useCart();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setOrdersLoading(true);
      try {
        const { data } = await customerApi.get("/customer/orders");
        setOrders(data || []);
      } catch { /* ignore */ }
      finally { setOrdersLoading(false); }
    })();
  }, [user]);

  if (!loading && !user) return <Navigate to="/login" replace state={{ next: "/account" }} />;
  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--hk-ink-3)]">Loading your account…</div>;
  }

  const totalSaved = orders.reduce((acc, o) => acc + (o.subtotal - o.total > 0 ? o.subtotal - o.total : 0), 0);

  const reorderFromOrder = (o) => {
    o.items.forEach((it) => {
      cart.addItem(it.slug, it.qty);
    });
    toast.success(`${o.items.length} item(s) added to cart`);
    nav("/cart");
  };

  const reorderItem = (row) => {
    cart.addItem(row.slug, row.qty || 1);
    toast.success("Added to cart");
    nav("/cart");
  };

  return (
    <>
      {/* Header hero */}
      <section className="relative pt-14 pb-8 lg:pt-20 lg:pb-10 overflow-hidden" data-testid="customer-account-page">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="label-eyebrow inline-flex items-center gap-1.5">
                <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" /> Your HerbKranti dashboard
              </div>
              <h1 className="serif mt-3 text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.05] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }} data-testid="account-welcome-heading">
                Welcome back, <span className="italic-accent text-[var(--hk-orange-deep)]">{user.name.split(" ")[0]}.</span>
              </h1>
              <p className="mt-3 text-[14px] text-[var(--hk-ink-2)]">Member since {fmtDate(user.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/products" className="hk-btn-ghost !py-2" data-testid="account-shop-link">
                <ShoppingBag size={14} /> Continue shopping
              </Link>
              <button onClick={() => { logout(); toast.success("Signed out"); nav("/"); }} className="hk-btn-ghost !py-2" data-testid="account-logout-btn">
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 grid sm:grid-cols-3 gap-4" data-testid="account-stats">
        <StatCard label="Total orders" value={orders.length} icon={Package} testId="account-stat-orders" />
        <StatCard label="You've saved" value={`₹${(totalSaved / 100).toLocaleString("en-IN")}`} sub="Across your purchases" icon={Repeat} testId="account-stat-savings" />
        <StatCard label="Reward points" value="Coming soon" sub="Available after our loyalty launch" icon={UserIcon} testId="account-stat-points" />
      </section>

      {/* Wellness timeline */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 mt-8">
        <WellnessTimeline orders={orders} onReorderItem={reorderItem} />
      </section>

      {/* Orders */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 mt-8">
        <div className="hk-card p-0 overflow-hidden" data-testid="account-orders-section">
          <div className="p-6 md:p-8 flex items-center justify-between gap-4 border-b border-[var(--hk-border)]">
            <div>
              <div className="label-eyebrow">Your history</div>
              <h3 className="serif mt-2 text-[22px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>Order history</h3>
            </div>
          </div>
          {ordersLoading ? (
            <div className="p-10 text-center text-[13.5px] text-[var(--hk-ink-3)]">Loading orders…</div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-gold)]/40 flex items-center justify-center text-[var(--hk-orange-deep)]">
                <Package size={20} />
              </div>
              <div className="serif mt-5 text-[22px] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>Your journey begins here.</div>
              <div className="mt-2 text-[14px] text-[var(--hk-ink-2)]">Every order you place will appear here — with one-click reorder.</div>
              <Link to="/products" className="hk-btn-primary mt-6 inline-flex" data-testid="account-empty-shop-btn">
                Explore products <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div>
              {orders.map((o) => (<OrderRow key={o.id} order={o} onReorder={reorderFromOrder} />))}
            </div>
          )}
        </div>
      </section>

      {/* Addresses */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 mt-8 mb-24">
        <AddressBook />
      </section>
    </>
  );
};
